import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import LeaderboardCard from "@/components/LeaderboardCard";
import { Card } from "@/components/ui/card";
import { formatDMY } from "@/lib/dateFormat";
import { badgeIcon, TIER_STYLES, CATEGORY_KEYS } from "@/lib/badges";
import { Trophy, Lock } from "lucide-react";

const CATEGORY_ORDER = ["attendance", "homework", "payments", "community", "special"];

export default function Achievements() {
  const { t, lang } = useI18n();
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/gamification/me"), api.get("/gamification/catalog")])
      .then(([a, b]) => { setMe(a.data); setCatalog(b.data || []); })
      .catch(() => { setMe({ points: 0, badges: [], progress: [] }); });
  }, []);

  const byId = useMemo(() => Object.fromEntries(catalog.map((c) => [c.id, c])), [catalog]);
  const earnedTop = useMemo(() => {
    const m = {};
    (me?.badges || []).forEach((b) => { if (!m[b.badge_id] || b.tier > m[b.badge_id].tier) m[b.badge_id] = b; });
    return m;
  }, [me]);
  const progressById = useMemo(() => Object.fromEntries((me?.progress || []).map((p) => [p.badge_id, p])), [me]);
  const L = (obj) => (obj?.[lang] ?? obj?.es ?? "");

  if (!me) return <div className="p-8 text-sm text-slate-500">…</div>;

  return (
    <div>
      <PageHeader title={t("my_achievements")} testId="achievements-header" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="p-6 lg:col-span-2 bg-brand-blue text-white border-0 relative overflow-hidden" data-testid="achievements-summary">
          <Trophy className="absolute -right-6 -bottom-6 h-40 w-40 text-white/10" />
          <div className="text-xs uppercase tracking-[0.15em] text-white/70">{t("my_points")}</div>
          <div className="font-display text-5xl font-bold mt-1" data-testid="achievements-points">{me.points}</div>
          <div className="flex flex-wrap gap-6 mt-4 text-sm text-white/85">
            <span>{t("badges_earned")}: <strong>{me.badges.length}</strong></span>
            {me.rank && <span>{t("your_rank")}: <strong>#{me.rank}</strong></span>}
          </div>
        </Card>
        <LeaderboardCard />
      </div>

      {CATEGORY_ORDER.map((cat) => {
        const items = catalog.filter((c) => c.category === cat);
        if (!items.length) return null;
        return (
          <section key={cat} className="mb-8" data-testid={`badge-category-${cat}`}>
            <h2 className="font-display text-lg font-semibold text-slate-900 mb-3">{t(CATEGORY_KEYS[cat])}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map((b) => {
                const Icon = badgeIcon(b.icon);
                const got = earnedTop[b.id];
                const prog = progressById[b.id];
                const tier = got?.tier || 0;
                const style = tier ? TIER_STYLES[Math.min(tier, 3)] : "bg-white border-slate-200 text-slate-400";
                const pct = prog?.next_threshold ? Math.min(100, Math.round((prog.current / prog.next_threshold) * 100)) : (tier ? 100 : 0);
                const count = (me.badges || []).filter((x) => x.badge_id === b.id).length;
                return (
                  <Card key={b.id} className={`p-4 flex gap-3 ${tier ? "" : "opacity-80"}`} data-testid={`badge-${b.id}`}>
                    <div className={`h-12 w-12 rounded-2xl border flex items-center justify-center shrink-0 ${style}`}>
                      {tier ? <Icon className="h-6 w-6" /> : <Lock className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-900 truncate">{L(b.name)}</p>
                        {b.tiers.length > 1 && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            {t("level")} {tier}/{b.tiers.length}
                          </span>
                        )}
                        {b.manual && count > 1 && <span className="text-[10px] font-bold text-slate-500">×{count}</span>}
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">{L(b.desc)}</p>
                      {prog && prog.next_threshold ? (
                        <div className="mt-2">
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-brand-blue rounded-full transition-[width]" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1">
                            {prog.current}/{prog.next_threshold} {L(b.unit)} · +{prog.next_points} {t("points_short")}
                          </p>
                        </div>
                      ) : got ? (
                        <p className="text-[11px] text-slate-500 mt-2">
                          {t("earned_on")} {formatDMY((got.awarded_at || "").slice(0, 10), lang)} · +{got.points} {t("points_short")}
                          {got.note ? ` · ${got.note}` : ""}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400 mt-2">{t("badge_manual_hint")}</p>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
